<template>
  <layout-admin current="threads">
    <div class="threads">
      <aside class="threads__list">
        <div class="threads__list-head">
          <h1>Threads</h1>
          <span>{{ threads.length }}</span>
        </div>
        <div class="threads__list-body">
          <div v-if="!threads.length" class="threads__empty">No threads yet.</div>
          <div
            v-for="thread in sortedThreads"
            :key="thread.id"
            class="threads__item"
            :class="{
              'is-active': selected && selected.id === thread.id,
              'is-pinned': isPinned(thread.id)
            }"
          >
            <button
              type="button"
              class="threads__pin"
              :class="{ 'is-on': isPinned(thread.id) }"
              :title="isPinned(thread.id) ? 'Unpin thread' : 'Pin thread'"
              @click.stop.prevent="togglePin(thread.id)"
            >
              {{ isPinned(thread.id) ? '★' : '☆' }}
            </button>
            <Link
              :href="route('admin.inbox.threads', thread.id)"
              class="threads__item-link"
            >
              <div class="threads__item-title">
                {{ thread.display_name || thread.external_user_id || ('Thread #' + thread.id) }}
              </div>
              <div class="threads__item-meta">
                <span v-if="thread.lead">{{ thread.lead.name || thread.lead.phone || ('Lead #' + thread.lead.id) }}</span>
                <span v-else>No lead</span>
                <span v-if="thread.lead && thread.lead.reply_mode === 'manual'" class="threads__manual">Manual</span>
                <span v-if="thread.last_message_at">{{ formatShort(thread.last_message_at) }}</span>
              </div>
            </Link>
          </div>
        </div>
      </aside>

      <section class="threads__chat">
        <template v-if="selected">
          <header class="threads__chat-head">
            <div>
              <h2>{{ selected.display_name || selected.external_user_id || ('Thread #' + selected.id) }}</h2>
              <p>
                #{{ selected.id }}
                <template v-if="selected.channel"> · {{ selected.channel }}</template>
                <template v-if="selected.lead">
                  ·
                  <Link :href="route('admin.inbox.leads')" class="threads__inline-link">
                    {{ selected.lead.name || selected.lead.phone || ('Lead #' + selected.lead.id) }}
                  </Link>
                  <span class="threads__status">{{ selected.lead.status }}</span>
                </template>
              </p>
            </div>
            <button
              type="button"
              class="threads__pin threads__pin--head"
              :class="{ 'is-on': isPinned(selected.id) }"
              :title="isPinned(selected.id) ? 'Unpin thread' : 'Pin thread'"
              @click="togglePin(selected.id)"
            >
              {{ isPinned(selected.id) ? '★' : '☆' }}
            </button>
          </header>

          <div v-if="selected.lead" class="threads__lead-summary">
            <p v-if="selected.lead.summary" class="threads__lead-summary-text">
              {{ selected.lead.summary }}
            </p>
            <p v-else class="threads__lead-summary-text is-muted">
              No lead summary yet.
            </p>
            <div class="threads__lead-facts">
              <span v-if="selected.lead.phone">{{ selected.lead.phone }}</span>
              <span v-if="selected.lead.email">{{ selected.lead.email }}</span>
              <span v-if="selected.lead.preferred_language">Lang: {{ selected.lead.preferred_language }}</span>
              <span v-if="selected.lead.satisfaction_score != null">Score: {{ selected.lead.satisfaction_score }}</span>
              <span v-if="nextAppointmentLabel">Next appt: {{ nextAppointmentLabel }}</span>
            </div>
            <div v-if="requirementEntries.length" class="threads__lead-reqs">
              <span
                v-for="item in requirementEntries"
                :key="item.key"
                class="threads__lead-req"
              >
                <strong>{{ item.key }}:</strong> {{ item.value }}
              </span>
            </div>
          </div>

          <div class="threads__messages" ref="messagesEl">
            <div v-if="!messages.length" class="threads__empty">No messages in this thread.</div>
            <div
              v-for="message in messages"
              :key="message.id"
              class="bubble"
              :class="message.direction === 'outbound' ? 'is-out' : 'is-in'"
            >
              <div class="bubble__body">
                <p v-if="visibleText(message)" class="bubble__text">{{ visibleText(message) }}</p>

                <div v-if="hasRichMedia(message)" class="bubble__media">
                  <img
                    v-if="isImage(message.media) && message.media.url"
                    :src="message.media.url"
                    :alt="message.media.caption || 'Image'"
                    class="bubble__image"
                  />
                  <audio
                    v-else-if="message.media.type === 'audio' && message.media.url"
                    :src="message.media.url"
                    controls
                    preload="metadata"
                  />
                  <video
                    v-else-if="message.media.type === 'video' && message.media.url"
                    :src="message.media.url"
                    controls
                    preload="metadata"
                    class="bubble__video"
                  />
                  <a
                    v-else-if="message.media.type === 'document' && message.media.url"
                    :href="message.media.url"
                    target="_blank"
                    rel="noopener"
                    class="bubble__doc"
                  >
                    Open document{{ message.media.mime ? ' (' + message.media.mime + ')' : '' }}
                  </a>
                  <pre
                    v-else-if="message.media.type === 'interactive'"
                    class="bubble__interactive"
                  >{{ formatInteractive(message.media) }}</pre>
                  <div v-else-if="message.media.url" class="bubble__media-fallback">
                    <a :href="message.media.url" target="_blank" rel="noopener">Open attachment</a>
                  </div>
                  <p v-if="message.media.caption" class="bubble__caption">{{ message.media.caption }}</p>
                </div>

                <div v-if="enrichmentText(message)" class="bubble__enrichment">
                  {{ enrichmentText(message) }}
                </div>
              </div>
              <div class="bubble__meta">
                <time>{{ formatShort(message.message_at) }}</time>
                <span class="bubble__category">{{ messageCategory(message) }}</span>
              </div>
            </div>
          </div>

          <footer v-if="selected.lead" class="threads__composer">
            <div class="threads__composer-bar">
              <span class="threads__mode" :class="{ 'is-manual': isManual }">{{ isManual ? 'Manual' : 'AI' }}</span>
              <button
                type="button"
                class="threads__mode-btn"
                :disabled="modeSaving"
                @click="setReplyMode(isManual ? 'ai' : 'manual')"
              >
                {{ isManual ? 'Hand back to AI' : 'Take over' }}
              </button>
            </div>
            <p v-if="modeError" class="threads__composer-error">{{ modeError }}</p>
            <template v-if="isManual">
              <form v-if="canReply" class="threads__composer-form" @submit.prevent="sendReply">
                <textarea
                  v-model="draft"
                  :maxlength="imageFile ? 1024 : 4096"
                  rows="3"
                  :placeholder="imageFile ? 'Caption (optional)' : 'Reply on WhatsApp'"
                  :disabled="replySending"
                />
                <div v-if="imagePreview" class="threads__preview">
                  <img :src="imagePreview" alt="" />
                  <button type="button" class="threads__attach" :disabled="replySending" @click="clearImage">
                    Remove image
                  </button>
                </div>
                <div class="threads__composer-row">
                  <span v-if="windowLabel">{{ windowLabel }}</span>
                  <div class="threads__composer-actions">
                    <input
                      ref="imageInput"
                      type="file"
                      accept="image/jpeg,image/png"
                      class="threads__file"
                      :disabled="replySending"
                      @change="onImage"
                    />
                    <button type="button" class="threads__attach" :disabled="replySending" @click="pickImage">
                      {{ imageFile ? 'Change image' : 'Attach image' }}
                    </button>
                    <button type="submit" class="threads__send" :disabled="replySending || !canSend">
                      {{ replySending ? 'Sending' : 'Send' }}
                    </button>
                  </div>
                </div>
                <p v-if="imageError" class="threads__composer-error">{{ imageError }}</p>
                <p v-if="replyError" class="threads__composer-error">{{ replyError }}</p>
              </form>
              <p v-else class="threads__composer-note">{{ replyBlockedReason }}</p>
            </template>
            <p v-else class="threads__composer-note">
              AI is replying on this lead. Take over to answer them yourself. That stops AI on every thread for this lead.
            </p>
          </footer>
        </template>

        <div v-else class="threads__placeholder">
          Select a thread to view the conversation.
        </div>
      </section>
    </div>
  </layout-admin>
</template>

<style lang="scss" scoped>
.threads {
  --border: var(--admin-border);
  --muted: var(--admin-muted);
  --surface: var(--admin-surface);

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--admin-radius);
  display: grid;
  flex: 1;
  grid-template-columns: minmax(240px, 320px) 1fr;
  min-height: 0;
  overflow: hidden;
}

.threads__list {
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.threads__list-head {
  align-items: baseline;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  z-index: 1;

  h1 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: var(--muted);
    font-size: 0.75rem;
  }
}

.threads__list-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.threads__item {
  align-items: flex-start;
  border-bottom: 1px solid #f4f4f5;
  color: inherit;
  display: flex;
  gap: 0.35rem;
  padding: 0.65rem 0.75rem 0.65rem 0.5rem;
  transition: background 0.12s ease;

  &:hover {
    background: #fafafa;
  }

  &.is-active {
    background: #f4f4f5;
  }

  &.is-pinned {
    background: #fffbeb;
  }

  &.is-pinned.is-active {
    background: #fef3c7;
  }
}

.threads__pin {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0.35rem;
  color: #a1a1aa;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.95rem;
  height: 1.75rem;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 1.75rem;

  &:hover {
    background: #f4f4f5;
    color: #ca8a04;
  }

  &.is-on {
    color: #ca8a04;
  }
}

.threads__pin--head {
  font-size: 1.1rem;
  height: 2rem;
  width: 2rem;
}

.threads__item-link {
  color: inherit;
  flex: 1;
  min-width: 0;
  text-decoration: none;
}

.threads__item-title {
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
}

.threads__item-meta {
  color: var(--muted);
  display: flex;
  font-size: 0.72rem;
  gap: 0.5rem;
  justify-content: space-between;
}

.threads__chat {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 0;
}

.threads__chat-head {
  align-items: flex-start;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-shrink: 0;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.9rem 1.1rem;

  h2 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem;
  }

  p {
    color: var(--muted);
    font-size: 0.78rem;
    margin: 0;
  }
}

.threads__inline-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.threads__status {
  background: #f4f4f5;
  border-radius: 999px;
  margin-left: 0.35rem;
  padding: 0.1rem 0.45rem;
}

.threads__lead-summary {
  background: #fafafa;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  padding: 0.75rem 1.1rem 0.85rem;
}

.threads__lead-summary-text {
  font-size: 0.84rem;
  line-height: 1.45;
  margin: 0 0 0.55rem;
  white-space: pre-wrap;

  &.is-muted {
    color: var(--muted);
  }
}

.threads__lead-facts {
  color: var(--muted);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.72rem;
  gap: 0.35rem 0.75rem;
  margin-bottom: 0.45rem;

  span:empty {
    display: none;
  }
}

.threads__lead-reqs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.threads__lead-req {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.7rem;
  max-width: 100%;
  overflow: hidden;
  padding: 0.2rem 0.55rem;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    font-weight: 600;
    text-transform: capitalize;
  }
}

.threads__messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1.1rem;
}

.threads__placeholder,
.threads__empty {
  color: var(--muted);
  font-size: 0.9rem;
  margin: auto;
  padding: 2rem;
  text-align: center;
}

.bubble {
  display: flex;
  flex-direction: column;
  max-width: min(72%, 560px);

  &.is-in {
    align-self: flex-start;
  }

  &.is-out {
    align-self: flex-end;

    .bubble__body {
      background: #18181b;
      color: #fafafa;
    }

    .bubble__meta {
      justify-content: flex-end;
    }

    .bubble__enrichment {
      background: rgb(255 255 255 / 10%);
      color: #d4d4d8;
    }

    .bubble__doc,
    .bubble__media-fallback a {
      color: #e4e4e7;
    }
  }
}

.bubble__body {
  background: #f4f4f5;
  border-radius: 1rem;
  padding: 0.7rem 0.85rem;
}

.bubble__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.bubble__media {
  margin-top: 0.4rem;
}

.bubble__image {
  border-radius: 0.5rem;
  display: block;
  max-height: 320px;
  max-width: 100%;
}

.bubble__video {
  border-radius: 0.5rem;
  max-width: 100%;
}

.bubble__doc {
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 500;
}

.bubble__interactive {
  background: rgb(0 0 0 / 6%);
  border-radius: 0.4rem;
  font-size: 0.72rem;
  margin: 0;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.5rem;
  white-space: pre-wrap;
}

.bubble__media-fallback {
  display: flex;
  font-size: 0.8rem;
  gap: 0.5rem;
}

.bubble__caption {
  color: inherit;
  font-size: 0.8rem;
  margin: 0.4rem 0 0;
  opacity: 0.85;
}

.bubble__enrichment {
  background: rgb(0 0 0 / 5%);
  border-radius: 0.4rem;
  font-size: 0.78rem;
  margin-top: 0.5rem;
  padding: 0.45rem 0.55rem;
  white-space: pre-wrap;
}

.bubble__time {
  color: inherit;
}

.bubble__meta {
  align-items: center;
  color: var(--muted);
  display: flex;
  font-size: 0.68rem;
  gap: 0.45rem;
  margin-top: 0.25rem;
  padding: 0 0.35rem;
}

.bubble__category {
  opacity: 0.85;
  text-transform: lowercase;

  &::before {
    content: '·';
    margin-right: 0.45rem;
  }
}

.threads__manual {
  color: #a16207;
  font-weight: 600;
}

.threads__composer {
  background: var(--surface);
  border-top: 1px solid var(--border);
  flex-shrink: 0;
  padding: 0.75rem 1.1rem 0.9rem;
}

.threads__composer-bar,
.threads__composer-row {
  align-items: center;
  display: flex;
  gap: 0.6rem;
  justify-content: space-between;
}

.threads__mode {
  background: #f4f4f5;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;

  &.is-manual {
    background: #fef3c7;
    color: #a16207;
  }
}

.threads__mode-btn,
.threads__send {
  background: #18181b;
  border: 0;
  border-radius: 0.4rem;
  color: #fff;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.75rem;

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.threads__composer-form {
  margin-top: 0.65rem;
}

.threads__composer textarea {
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  display: block;
  font: inherit;
  font-size: 0.88rem;
  min-height: 4.5rem;
  padding: 0.55rem 0.7rem;
  resize: vertical;
  width: 100%;
}

.threads__composer-row {
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.threads__composer-actions {
  display: flex;
  flex-shrink: 0;
  gap: 0.45rem;
}

.threads__file {
  display: none;
}

.threads__attach {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 0.4rem;
  color: inherit;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.75rem;

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.threads__preview {
  align-items: center;
  display: flex;
  gap: 0.65rem;
  margin-top: 0.55rem;

  img {
    border-radius: 0.4rem;
    display: block;
    height: 4.5rem;
    object-fit: cover;
    width: 4.5rem;
  }
}

.threads__composer-row span,
.threads__composer-note {
  color: var(--muted);
  font-size: 0.75rem;
  line-height: 1.4;
  margin: 0.45rem 0 0;
}

.threads__composer-error {
  color: #b91c1c;
  font-size: 0.75rem;
  line-height: 1.4;
  margin: 0.4rem 0 0;
}

@media (max-width: 640px) {
  .threads {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(180px, 36%) 1fr;
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import LayoutAdmin from '@/Layouts/Admin.vue';

const PIN_STORAGE_KEY = 'admin_inbox_pinned_thread_ids';

export default {
  components: {
    LayoutAdmin,
    Link
  },

  props: {
    threads: { type: Array, required: true },
    selected: { type: Object, default: null },
    messages: { type: Array, default: () => [] }
  },

  data() {
    return {
      pinnedIds: [],
      draft: '',
      imageFile: null,
      imagePreview: '',
      imageError: '',
      modeSaving: false,
      replySending: false
    };
  },

  computed: {
    sortedThreads() {
      const pinned = new Set(this.pinnedIds);
      return this.threads.slice().sort((a, b) => {
        const aPinned = pinned.has(Number(a.id)) ? 1 : 0;
        const bPinned = pinned.has(Number(b.id)) ? 1 : 0;
        if (aPinned !== bPinned) return bPinned - aPinned;
        return 0;
      });
    },

    requirementEntries() {
      const lead = this.selected && this.selected.lead;
      const requirements = lead && lead.requirements && typeof lead.requirements === 'object'
        ? lead.requirements
        : {};
      return Object.keys(requirements).map((key) => {
        const raw = requirements[key];
        let value = '';
        if (Array.isArray(raw)) {
          value = raw.join(', ');
        } else if (raw && typeof raw === 'object') {
          try {
            value = JSON.stringify(raw);
          } catch (e) {
            value = String(raw);
          }
        } else if (raw != null) {
          value = String(raw);
        }
        return {
          key: String(key).replace(/_/g, ' '),
          value
        };
      }).filter((item) => item.value !== '');
    },

    isManual() {
      return !!(this.selected && this.selected.lead && this.selected.lead.reply_mode === 'manual');
    },

    canSend() {
      return !!(this.draft.trim() || this.imageFile);
    },

    canReply() {
      const serviceWindow = this.selected && this.selected.service_window;
      return this.isManual && this.selected && this.selected.channel === 'whatsapp' && !!(serviceWindow && serviceWindow.open);
    },

    replyBlockedReason() {
      if (!this.selected || this.selected.channel !== 'whatsapp') {
        return 'Manual replies from this panel use the WhatsApp 24-hour window. This thread is not WhatsApp.';
      }
      return 'The 24-hour WhatsApp window is closed. They need to message again before a free-form reply can be sent.';
    },

    windowLabel() {
      const closesAt = this.selected && this.selected.service_window && this.selected.service_window.closes_at;
      if (!closesAt) return '';
      return 'Window open until ' + this.formatShort(closesAt);
    },

    pageErrors() {
      return (this.$page && this.$page.props && this.$page.props.errors) || {};
    },

    replyError() {
      return this.pageErrors.reply || this.pageErrors.text || null;
    },

    modeError() {
      return this.pageErrors.reply_mode || null;
    },

    nextAppointmentLabel() {
      const lead = this.selected && this.selected.lead;
      const appointments = lead && Array.isArray(lead.appointments) ? lead.appointments : [];
      const next = appointments.find((item) => item && item.status === 'scheduled') || appointments[0];
      if (!next) return '';
      const when = next.starts_at ? this.formatShort(next.starts_at) : (next.preferred_time_text || '');
      if (!when) return next.status || '';
      return when + (next.status ? (' (' + next.status + ')') : '');
    }
  },

  created() {
    this.pinnedIds = this.loadPinnedIds();
  },

  beforeUnmount() {
    this.clearImage();
  },

  watch: {
    'selected.id': function () {
      this.draft = '';
      this.clearImage();
    },
    messages: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          const el = this.$refs.messagesEl;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    }
  },

  methods: {
    loadPinnedIds() {
      try {
        const raw = localStorage.getItem(PIN_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);
      } catch (e) {
        return [];
      }
    },

    savePinnedIds() {
      try {
        localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(this.pinnedIds));
      } catch (e) {
        // Ignore quota / private-mode failures.
      }
    },

    isPinned(threadId) {
      return this.pinnedIds.indexOf(Number(threadId)) !== -1;
    },

    setReplyMode(mode) {
      if (!this.selected || this.modeSaving) return;
      this.modeSaving = true;
      this.$inertia.post(route('admin.inbox.threads.reply-mode', this.selected.id), {
        reply_mode: mode
      }, {
        preserveScroll: true,
        onFinish: () => {
          this.modeSaving = false;
        }
      });
    },

    pickImage() {
      if (this.$refs.imageInput) this.$refs.imageInput.click();
    },

    onImage(event) {
      const file = event.target.files && event.target.files[0];
      if (event.target) event.target.value = '';
      if (!file) return;
      const mime = String(file.type || '');
      if (mime !== 'image/jpeg' && mime !== 'image/png') {
        this.imageError = 'Use a JPEG or PNG image.';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.imageError = 'Image must be 5 MB or smaller.';
        return;
      }
      this.clearImage();
      this.imageError = '';
      this.imageFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },

    clearImage() {
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview);
      this.imageFile = null;
      this.imagePreview = '';
    },

    sendReply() {
      const text = this.draft.trim();
      if (this.replySending || !this.selected || (!text && !this.imageFile)) return;
      const body = new FormData();
      if (text) body.append('text', text);
      if (this.imageFile) body.append('image', this.imageFile);
      this.replySending = true;
      this.$inertia.post(route('admin.inbox.threads.reply', this.selected.id), body, {
        preserveScroll: true,
        forceFormData: true,
        onSuccess: () => {
          this.draft = '';
          this.clearImage();
        },
        onFinish: () => {
          this.replySending = false;
        }
      });
    },

    visibleText(message) {
      const text = message && message.text ? String(message.text).trim() : '';
      const caption = message && message.media && message.media.caption
        ? String(message.media.caption).trim()
        : '';
      if (text && caption && text === caption) return '';
      return message && message.text ? message.text : '';
    },

    togglePin(threadId) {
      const id = Number(threadId);
      if (!Number.isFinite(id) || id <= 0) return;
      if (this.isPinned(id)) {
        this.pinnedIds = this.pinnedIds.filter((pinnedId) => pinnedId !== id);
      } else {
        this.pinnedIds = this.pinnedIds.concat([id]);
      }
      this.savePinnedIds();
    },

    formatShort(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    isImage(media) {
      if (!media) return false;
      if (media.type === 'image' || media.type === 'sticker') return true;
      const mime = String(media.mime || '');
      return mime.indexOf('image/') === 0;
    },

    hasRichMedia(message) {
      const media = message && message.media;
      if (!media) return false;
      const type = String(media.type || '').toLowerCase();
      if (!type || type === 'text') return false;
      if (this.isImage(media) && media.url) return true;
      if (type === 'audio' && media.url) return true;
      if (type === 'video' && media.url) return true;
      if (type === 'document' && media.url) return true;
      if (type === 'interactive') return true;
      return !!media.url;
    },

    messageCategory(message) {
      const type = message && message.media && message.media.type
        ? String(message.media.type).trim()
        : '';
      return type || 'text';
    },

    formatInteractive(media) {
      try {
        return JSON.stringify(media.payload || media, null, 2);
      } catch (e) {
        return String(media.type || 'interactive');
      }
    },

    enrichmentText(message) {
      const enrichment = message && message.enrichment;
      if (!enrichment || typeof enrichment !== 'object') return '';
      return (
        enrichment.agent_text ||
        enrichment.summary ||
        enrichment.text ||
        ''
      );
    }
  }
};
</script>
